<script setup>
import vSelect from 'vue3-select'
// import 'vue3-select/dist/vue3-select.css'

const props = defineProps({
  fieldClass: {
    type: String,
    default: 'field'
  },
  labelName: {
    type: String,
    required: true
  },
  ariaLabel: {
    type: String,
    default: ''
  },
  placeholder: {
    type: String,
    default: ''
  },
  labelSubtext: {
    type: String,
    default: ''
  },
  required: {
    type: Boolean,
    default: false
  },
  modelValue: {
    type: [String, Number, Object, null],
    default: null
  },
  lookupOptions: {
    type: Object,
    required: true
  },
  returnFormat: {
    type: Function,
    required: true
  },
  optionLabel: {
    type: Function,
    required: true
  },
  showCreateButton: {
    type: Boolean,
    default: false
  },
  error: {
    type: String,
    default: ''
  }
})

const emit = defineEmits([
  'update:modelValue',
  'showModal'
])

function canCreateFromSearch(search) {
  return props.showCreateButton && String(search || '').trim().length >= 3
}

function showCreateModal() {
  emit('showModal')
}
</script>

<template>
  <component
    :is="labelName ? 'label' : 'div'"
    :class="[fieldClass, { 'has-error': error }]"
  >
    <span v-if="labelName">
      {{ labelName }}<span v-if="required" class="required marker" aria-hidden="true">*</span>
    </span>

    <small
      v-if="labelSubtext"
      class="small muted text"
    >
      {{ labelSubtext }}
    </small>

    <v-select
      :model-value="props.modelValue"
      :options="lookupOptions.options.value"
      :filterable="false"
      :loading="lookupOptions.loading.value"
      :get-option-label="optionLabel"
      :reduce="returnFormat"
      class="w-full"
      :class="{ 'has-value': !!props.modelValue }"
      :aria-label="ariaLabel || labelName || 'Search for an option'"
      :placeholder="placeholder"
      @update:model-value="val => emit('update:modelValue', val)"
      @search="lookupOptions.onSearch"
    >
      <template #search="{ attributes, events }">
        <input
          class="vs__search"
          v-bind="attributes"
          :aria-label="ariaLabel || labelName || 'Search for an option'"
          :aria-required="required"
          :placeholder="placeholder || attributes.placeholder"
          v-on="events"
        >
      </template>

      <template #open-indicator>
        🔍
      </template>

      <template #list-footer="{ search }">
        <li
          v-if="canCreateFromSearch(search)"
          class="vs__dropdown-option p-sm"
        >
          <button
            type="button"
            class="small primary solid full button"
            @mousedown.prevent.stop="showCreateModal"
          >
            Create one
          </button>
        </li>
      </template>

      <template #no-options="{ search }">
        <div
          v-if="String(search || '').trim().length < 3"
          class="small muted text p-sm"
        >
          Type at least 3 characters to search...
        </div>

        <div
          v-else
          class="small text p-sm"
        >
          No match for "<strong>{{ search }}</strong>"
        </div>
      </template>
    </v-select>

    <small
      v-if="error"
      class="small danger text"
    >
      {{ error }}
    </small>
  </component>
</template>
